import { AfterViewInit, Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { Validators } from '@angular/forms'
import { applyEach, disabled, FieldTree, form, required, validate } from '@angular/forms/signals'
import { ActivatedRoute } from '@angular/router'

import { Subject, combineLatest, of } from 'rxjs'
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators'

import { MatDialog } from '@angular/material/dialog'
import { Ajoneuvo, KirjanpidonUlkopuolisetKulut } from '@lemon/core-firebase/model/kayttaja'
import {
  AnnaKayttajanTiedotReq,
  KirjanpidonUlkopuolisetKulutAnnaKayttajanTiedotResp,
  MagicLinkErrorResponse,
  TallennaKirjanpidonUlkopuolisetKulutReq,
  TallennaKirjanpidonUlkopuolisetKulutResp,
} from '@lemon/core-firebase/model/magic-link'
import { TuettuKieli } from '@lemon/core/model/common'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { KayttajaService } from '../_angular/service/kayttaja.service'
import { arvonMuutokset, asetaArvo } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'
import { VaihdaKieliDialog, VaihdaKieliDialogData } from '../vaihda-kieli.dialog'

interface AjoneuvoLomake {
  rekisteritunnus: string
  tyyppi: Ajoneuvo['tyyppi'] | null
  kokonaisKmMaara: number | null
  tyoAjoissaKm: number | null
  tyoAjoissaKmLiianSuuri: boolean
}

interface UlkopuolisetKulutLomake {
  eiIlmoitettavaa: boolean
  ajoneuvot: AjoneuvoLomake[]
  tiedotPerustuvat: KirjanpidonUlkopuolisetKulut['tiedotPerustuvat'] | null
  yli10hKotimaanMatkojenLukumaara: number | null
  yli6hKotimaanMatkojenLukumaara: number | null
  ulkomaanMatkapaivienLukumaara: number | null
  ulkomaanPaivarahojenYhteismaara: number | null
  kotiYrityksenTyotilana: KirjanpidonUlkopuolisetKulut['kotiYrityksenTyotilana'] | null
  vakuutan: boolean
  sitoudun: boolean
}

@Component({
  templateUrl: './kirjanpidon-ulkopuoliset-kulut.component.html',
  styleUrls: ['../login/login.component.css', './kirjanpidon-ulkopuoliset-kulut.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjanpidonUlkopuolisetKulutComponent implements OnInit, AfterViewInit, OnDestroy {
  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)
  private readonly _tiedotPerustuvatPoissa = signal(false)
  private readonly _sitoudunPoissa = signal(false)

  lomake = form(
    signal<UlkopuolisetKulutLomake>({
      eiIlmoitettavaa: false,
      ajoneuvot: [],
      tiedotPerustuvat: null,
      yli10hKotimaanMatkojenLukumaara: null,
      yli6hKotimaanMatkojenLukumaara: null,
      ulkomaanMatkapaivienLukumaara: null,
      ulkomaanPaivarahojenYhteismaara: null,
      kotiYrityksenTyotilana: 'ei-vahennysta',
      vakuutan: false,
      sitoudun: false,
    }),
    polku => {
      applyEach(polku.ajoneuvot, rivi => {
        required(rivi.rekisteritunnus)
        required(rivi.tyyppi)
        required(rivi.kokonaisKmMaara)
        required(rivi.tyoAjoissaKm)
        validate(rivi.tyoAjoissaKm, ({ valueOf }) => (valueOf(rivi.tyoAjoissaKmLiianSuuri) ? { kind: 'toolarge' } : null))
      })
      required(polku.tiedotPerustuvat)
      disabled(polku.tiedotPerustuvat, () => this._tiedotPerustuvatPoissa())
      validoiReaktiivisesti(polku.vakuutan, Validators.requiredTrue)
      validoiReaktiivisesti(polku.sitoudun, Validators.requiredTrue)
      disabled(polku.sitoudun, () => this._sitoudunPoissa())
    },
  )
  year = new Date().getFullYear()
  readonly message = signal<string>(undefined)
  readonly commonError = signal<string>(undefined)
  readonly showForm = signal(false)
  readonly isUsedLinkError = signal(false)
  readonly asiakkaanNimi = signal<string>(undefined)
  readonly verovuosi = signal<number>(undefined)
  readonly onIlmoitettavaa = signal(true)
  readonly maakohtaisetPaivarahatLinkki = signal<string>(undefined)
  readonly formTextType = signal<string>(undefined)

  readonly lomakkeenLahettamisenLoppupaiva = signal<string>(undefined)
  readonly kokopaivaraha = signal<string>(undefined)
  readonly osapaivaraha = signal<string>(undefined)
  readonly kilometrikorvaus = signal<string>(undefined)

  paasaantoinenKotiVahennys: string = '980'
  osaaikainenKotiVahennys: string = '490'
  satunnainenKotiVahennys: string = '245'

  private _ajoneuvot: Ajoneuvo[] = []
  private _ajoneuvojenLopetukset: Subject<void>[] = []

  // onkoTyoajoissaYliPuoliTextObservable: Observable<string>
  private _token: string
  private _submitStarted: boolean

  constructor(
    private _errorHandler: ErrorHandler,
    private _ladataanService: LadataanService,
    private _route: ActivatedRoute,
    private _validationService: FormValidationService,
    private _kayttaService: KayttajaService,
    private _dialog: MatDialog,
    private _translationService: LemonTranslationService,
    private _firebaseLemonaid: FirebaseLemonaid,
  ) {
    this._kayttaService.logout()
  }

  ngOnInit() {
    this.maakohtaisetPaivarahatLinkki.set(this._getMaakohtaisetPaivarahatLinkki('fi', 2025))
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  onkoMuokattuTaiKosketettu<T>(kentta: FieldTree<T>): boolean {
    return kentta().dirty() || kentta().touched()
  }

  poistaAjoneuvo(index: number) {
    this._lopetaAjoneuvonTilaukset(index)
    this._asetaAjoneuvot(
      this.lomake
        .ajoneuvot()
        .value()
        .filter((_, i) => i !== index),
    )
    this._ajoneuvot.splice(index, 1)
  }

  private _lopetaAjoneuvonTilaukset(index: number) {
    const [lopetus] = this._ajoneuvojenLopetukset.splice(index, 1)
    lopetus?.next()
    lopetus?.complete()
  }

  // Rivin lisäys ja poisto eivät lähettäneet rivin kenttien valueChanges-tapahtumia, vain taulukon
  private _asetaAjoneuvot(ajoneuvot: AjoneuvoLomake[]) {
    asetaArvo(this.lomake.ajoneuvot, ajoneuvot, { ilmoita: false })
    this._ajoneuvotMuuttuivat(ajoneuvot)
  }

  private _ajoneuvotMuuttuivat(ajoneuvot: AjoneuvoLomake[]) {
    if (!ajoneuvot?.length) {
      asetaArvo(this.lomake.tiedotPerustuvat, null)
      this._tiedotPerustuvatPoissa.set(true)
    } else {
      this._tiedotPerustuvatPoissa.set(false)
    }
  }

  lisaaAjoneuvo(prefill?: Ajoneuvo) {
    const ajoneuvo: Ajoneuvo = {
      rekisteritunnus: prefill ? prefill.rekisteritunnus : '',
      tyyppi: prefill ? prefill.tyyppi : null,
      kokonaisKmMaara: prefill ? prefill.kokonaisKmMaara : null,
      tyoAjoissaKm: prefill ? prefill.tyoAjoissaKm : null,
    }

    const index = this.lomake.ajoneuvot().value().length
    this._asetaAjoneuvot([
      ...this.lomake.ajoneuvot().value(),
      {
        rekisteritunnus: ajoneuvo.rekisteritunnus ?? '',
        tyyppi: ajoneuvo.tyyppi ?? null,
        kokonaisKmMaara: ajoneuvo.kokonaisKmMaara ?? null,
        tyoAjoissaKm: ajoneuvo.tyoAjoissaKm ?? null,
        tyoAjoissaKmLiianSuuri: false,
      },
    ])
    const rivi = this.lomake.ajoneuvot[index]
    const lopetus = new Subject<void>()
    this._ajoneuvojenLopetukset.push(lopetus)
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(lopetus))

    // Työajojen virhe päivittyi vain kilometrien muuttuessa, ei esitäytetyille arvoille
    const paivitaTyoajojenVirhe = () => asetaArvo(rivi.tyoAjoissaKmLiianSuuri, ajoneuvo.kokonaisKmMaara < ajoneuvo.tyoAjoissaKm)

    muutokset(rivi.rekisteritunnus).subscribe(value => {
      ajoneuvo.rekisteritunnus = value || null
    })
    muutokset(rivi.tyyppi).subscribe(value => {
      ajoneuvo.tyyppi = value || null
    })
    muutokset(rivi.kokonaisKmMaara).subscribe(value => {
      ajoneuvo.kokonaisKmMaara = value || null
      paivitaTyoajojenVirhe()
    })
    muutokset(rivi.tyoAjoissaKm).subscribe(value => {
      ajoneuvo.tyoAjoissaKm = value || null
      paivitaTyoajojenVirhe()
    })

    this._ajoneuvot.push(ajoneuvo)
  }

  ngAfterViewInit() {
    arvonMuutokset(this.lomake.ajoneuvot, { injector: this._injector })
      .pipe(startWith([]), takeUntil(this._ngUnsubscribe))
      .subscribe(value => this._ajoneuvotMuuttuivat(value))

    const kayttajanTiedotObservable = this._route.paramMap.pipe(
      map(paramMap => paramMap.get('token')),
      switchMap(token => {
        if (token) {
          this._token = token
          this._ladataanService.aloitaLataaminen()
          const requestData: AnnaKayttajanTiedotReq = {
            token: token,
          }
          return this._firebaseLemonaid.functionsCall<AnnaKayttajanTiedotReq, KirjanpidonUlkopuolisetKulutAnnaKayttajanTiedotResp>('ajopaivakirjaAnnaKayttajaTiedot', requestData)
        }
        return of<KirjanpidonUlkopuolisetKulutAnnaKayttajanTiedotResp>(null)
      }),
    )

    combineLatest([kayttajanTiedotObservable, this._translationService.currentLanguageObservable])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe({
        next: ([response, kieli]) => {
          if (response && !response.e) {
            if (response.lang) {
              this._translationService.vaihdaKieli(response.lang)
            }
            this.verovuosi.set(response.verovuosi)
            this.showForm.set(true)
            this.asiakkaanNimi.set(response.nimi)
            this.formTextType.set(!response.onkoHolvi ? 'regular' : 'holvi.' + (response.subtype || 'yearly'))

            this.kokopaivaraha.set('53') // 2025
            this.osapaivaraha.set('24') // 2025
            this.kilometrikorvaus.set('0,59') // 2025
            this.maakohtaisetPaivarahatLinkki.set(this._getMaakohtaisetPaivarahatLinkki(kieli, response.verovuosi))
            this.lomakkeenLahettamisenLoppupaiva.set(this._getLoppupaiva(response.onkoHolvi, response.verovuosi || 2025, response.subtype || 'yearly'))

            this._prefillForm(response.prefillData)
          } else if (response && [MagicLinkErrorResponse.USED, MagicLinkErrorResponse.EXPIRED].includes(response.e)) {
            this.isUsedLinkError.set(response.e === MagicLinkErrorResponse.USED)
            if (this.isUsedLinkError()) {
              this.message.set('ulkopuoliset-kulut.submit-successful')
            } else if (response.e === MagicLinkErrorResponse.EXPIRED) {
              this.message.set('magic-link.expired-ajopaivakirja-link-' + (response.onkoHolvi ? 'holvi' : 'regular'))
            }
            this._errorHandler.handleError(response.e)
          } else {
            this.showForm.set(true)
            this.commonError.set('yleiset.tuntematon-virhe')
            this._errorHandler.handleError(new Error(response ? response.e : 'unknown-error'))
          }
          this._ladataanService.lopetaLataaminen()
        },
        error: err => {
          this.showForm.set(true)
          this.commonError.set('yleiset.tuntematon-virhe')
          this._errorHandler.handleError(err)
        },
      })

    arvonMuutokset(this.lomake.eiIlmoitettavaa, { injector: this._injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(eiIlmoitettavaa => {
        this.onIlmoitettavaa.set(!eiIlmoitettavaa)

        if (eiIlmoitettavaa) {
          this._tiedotPerustuvatPoissa.set(true)
          this._sitoudunPoissa.set(true)
        } else {
          this._sitoudunPoissa.set(false)
          const onkoAjoneuvoja = !!this.lomake.ajoneuvot().value()?.length

          if (onkoAjoneuvoja) {
            this._tiedotPerustuvatPoissa.set(false)
          }
        }
      })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  vaihdaKieli() {
    const data: VaihdaKieliDialogData = {
      tallenna: false,
    }
    this._dialog.open(VaihdaKieliDialog, { data: data })
  }

  async submit() {
    if (this._submitStarted) {
      return
    }
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this._submitStarted = true
    this._ladataanService.aloitaLataaminen()

    try {
      const arvot = this.lomake().value()
      const saveRequestData: TallennaKirjanpidonUlkopuolisetKulutReq = {
        token: this._token,
        vuosi: this.verovuosi(),
        ajoneuvot: this.onIlmoitettavaa() ? this._ajoneuvot : [],
        tiedotPerustuvat: this.onIlmoitettavaa() ? arvot.tiedotPerustuvat : null,
        yli10hKotimaanMatkojenLukumaara: this.onIlmoitettavaa() ? arvot.yli10hKotimaanMatkojenLukumaara : null,
        yli6hKotimaanMatkojenLukumaara: this.onIlmoitettavaa() ? arvot.yli6hKotimaanMatkojenLukumaara : null,
        ulkomaanMatkapaivienLukumaara: this.onIlmoitettavaa() ? arvot.ulkomaanMatkapaivienLukumaara : null,
        ulkomaanPaivarahojenYhteismaara: this.onIlmoitettavaa() ? arvot.ulkomaanPaivarahojenYhteismaara : null,
        kotiYrityksenTyotilana: this.onIlmoitettavaa() ? arvot.kotiYrityksenTyotilana : 'ei-vahennysta',
        eiIlmoitettavaa: arvot.eiIlmoitettavaa || null,
      }

      const response = await this._firebaseLemonaid.functionsCall<TallennaKirjanpidonUlkopuolisetKulutReq, TallennaKirjanpidonUlkopuolisetKulutResp>('ajopaivakirjaTallennus', saveRequestData)
      if (response && !response.e) {
        this.showForm.set(false)
        this.isUsedLinkError.set(true)
        this.message.set('ulkopuoliset-kulut.submit-successful')
      } else if (response && [MagicLinkErrorResponse.USED, MagicLinkErrorResponse.EXPIRED].includes(response.e)) {
        this.showForm.set(false)
        this.isUsedLinkError.set(response.e === MagicLinkErrorResponse.USED)
        if (this.isUsedLinkError()) {
          this.message.set('ulkopuoliset-kulut.submit-successful')
        } else {
          this.message.set('magic-link.' + response.e)
        }
        this._errorHandler.handleError(response.e)
      } else {
        this.commonError.set('yleiset.tuntematon-virhe')
        this._errorHandler.handleError(response ? response.e : 'no-response')
      }
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._submitStarted = false
    }
  }

  private _prefillForm(ulkopuolisetKulut: KirjanpidonUlkopuolisetKulut) {
    if (!ulkopuolisetKulut) {
      return
    }
    this._ajoneuvot = []
    while (this._ajoneuvojenLopetukset.length) {
      this._lopetaAjoneuvonTilaukset(0)
    }
    this._asetaAjoneuvot([])

    // NB! Don't change order for these - timing issue with subscriptions
    if (ulkopuolisetKulut.ajoneuvot?.length) {
      for (const ajoneuvo of ulkopuolisetKulut.ajoneuvot) {
        this.lisaaAjoneuvo(ajoneuvo)
      }
    }

    // Signal Forms poistaa kentän, jonka arvo on undefined, joten puuttuva esitäyttö tallennetaan null-arvona
    asetaArvo(this.lomake.tiedotPerustuvat, ulkopuolisetKulut.tiedotPerustuvat ?? null)
    asetaArvo(this.lomake.yli10hKotimaanMatkojenLukumaara, ulkopuolisetKulut.yli10hKotimaanMatkojenLukumaara ?? null)
    asetaArvo(this.lomake.yli6hKotimaanMatkojenLukumaara, ulkopuolisetKulut.yli6hKotimaanMatkojenLukumaara ?? null)
    asetaArvo(this.lomake.ulkomaanMatkapaivienLukumaara, ulkopuolisetKulut.ulkomaanMatkapaivienLukumaara ?? null)
    asetaArvo(this.lomake.ulkomaanPaivarahojenYhteismaara, ulkopuolisetKulut.ulkomaanPaivarahojenYhteismaara ?? null)
    asetaArvo(this.lomake.kotiYrityksenTyotilana, ulkopuolisetKulut.kotiYrityksenTyotilana ?? null)
  }

  // private onkoTyoajoissaYliPuoli(kokonaisKmMaara: number, tyoajoissaKm: number): boolean | null {
  //   if (this._validationService.processValue(kokonaisKmMaara) !== null &&
  //     this._validationService.processValue(tyoajoissaKm) !== null) {

  //     return tyoajoissaKm / kokonaisKmMaara * 100 > 50
  //   }
  //   return null
  // }
  // private getTyoajoissaYliPuoliText(onkoYliPuoli: boolean | null) {
  //   if (this._validationService.processValue(onkoYliPuoli) === null) {
  //     return ''
  //   }
  //   if (onkoYliPuoli) {
  //     return 'Työajoa yli 50%.'
  //   }
  //   return 'Työajoa alle 50%.'
  // }
  private _getLoppupaiva(onkoHolvi: boolean, verovuosi: number, subtype: 'yearly' | 'half-year') {
    if (!onkoHolvi) {
      return '15.01.' + (verovuosi + 1)
    }

    if (subtype === 'half-year') {
      return '05.08.' + verovuosi
    }

    return '15.01.' + (verovuosi + 1)
  }

  private _getMaakohtaisetPaivarahatLinkki(kieli: TuettuKieli, verovuosi: number): string {
    if (kieli === 'en') {
      return 'https://www.vero.fi/en/detailed-guidance/decisions/47405/tax-exempt-allowances-in-' + (verovuosi || '2025') + '-for-business-travel/'
    }
    return 'https://www.vero.fi/syventavat-vero-ohjeet/paatokset/47405/verohallinnon-paatos-verovapaista-matkakustannusten-korvauksista-vuonna-' + verovuosi || '2025'
  }
}
